/**
 * Cross-app theme core. Three-state model:
 *   'system' (default) — follow the device's prefers-color-scheme, live.
 *   'light' / 'dark'   — explicit user choice.
 *
 * Persistence:
 *   localStorage 'eb-theme'         — this device's explicit choice (unset =
 *                                     the user never chose on this device).
 *   localStorage 'eb-theme-account' — a mirror of user_settings.theme_preference,
 *                                     refreshed on app boot, so the pre-paint
 *                                     script (themeScript.ts) can honor the
 *                                     account value without a network call.
 *
 * Precedence everywhere (script + provider must agree):
 *   device 'eb-theme' > account mirror > 'system'.
 */

export type ThemeMode = 'system' | 'light' | 'dark';

export const THEME_KEY = 'eb-theme';
export const ACCOUNT_THEME_KEY = 'eb-theme-account';

function validMode(v: string | null): ThemeMode | null {
  return v === 'system' || v === 'light' || v === 'dark' ? v : null;
}

export function getStoredMode(): ThemeMode | null {
  try {
    return validMode(localStorage.getItem(THEME_KEY));
  } catch {
    return null;
  }
}

export function setStoredMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_KEY, mode);
  } catch {
    /* storage unavailable — theme still applies for the session */
  }
}

export function getAccountMirror(): ThemeMode | null {
  try {
    return validMode(localStorage.getItem(ACCOUNT_THEME_KEY));
  } catch {
    return null;
  }
}

export function setAccountMirror(mode: ThemeMode | null): void {
  try {
    if (mode === null) localStorage.removeItem(ACCOUNT_THEME_KEY);
    else localStorage.setItem(ACCOUNT_THEME_KEY, mode);
  } catch {
    /* ignore */
  }
}

/** The mode that takes effect on this device right now. */
export function effectiveMode(): ThemeMode {
  return getStoredMode() ?? getAccountMirror() ?? 'system';
}

export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return typeof matchMedia !== 'undefined' &&
      matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

export function applyResolvedTheme(t: 'light' | 'dark'): void {
  const root = document.documentElement;
  root.classList.toggle('dark', t === 'dark');
  root.style.colorScheme = t;
}
